/* eslint-disable no-param-reassign */
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  SearchResultSection,
  PersonLinkedinSearch,
} from '../Types/GlobalSearchResultsTypes';
import {
  AdvanceSearchRequest,
  AdvanceSearchState,
  AdvancedSearchMetadataRecord,
  AdvanceSearchDropdownOption,
  AdvanceSearchPeopleForm,
  AdvanceSearchCompanyForm,
  AdvanceSearchBrmForm,
} from '../Types/AdvanceSearchResultsTypes';
import { FollowCompanyResponse, Company } from 'Types/CompaniesTypes';
import { FollowCompanyRequest } from 'Types/RequestBodies';
import { Executive } from 'Types/PersonTypes';
import { searchTypeToTabIndex } from 'Utils/UtilityFunctions';

const SectionTypes = {
  Executives: 'Executives',
  Companies: 'Companies',
  BuyerGroups: 'Buyer Groups',
};

export const initialState: AdvanceSearchState = {
  searchType: '',
  searchParams: null,
  page: 1,
  isLoading: false,
  isLoaded: false,
  isPagination: false,
  isPersonExtendedLoading: false,
  isPersonExtendedLoaded: false,
  errorText: '',
  peopleSection: {
    title: 'Matching People',
    type: SectionTypes.Executives,
    count: 0,
    firstCount: 0,
    persons: [],
    followedPersons: [],
  },
  companiesSection: {
    title: 'Companies',
    type: SectionTypes.Companies,
    count: 0,
    firstCount: 0,
    companies: [],
    followedCompanies: [],
  },
  buyerGroupsSection: {
    title: 'Buyer Groups',
    type: SectionTypes.BuyerGroups,
    count: 0,
    firstCount: 0,
    groups: [],
  },
  memberOptions: [],
  memberLoading: false,
  memberHasMore: false,
  memberOffset: 0,
  memberTotal: 0,
  memberOptionsLoaded: false,
  memberSearchTerm: '',
  peopleForm: {
    fullName: '',
    currentCo: '',
    previousCo: '',
    designation: '',
    country: '',
    region: '',
  },
  companyForm: {
    name: '',
    industry: '',
    revenue: '',
    country: '',
    region: '',
    size: '',
  },
  brmForm: {
    name: '',
    member: 0,
    size: '',
    sharedBy: 0,
  },
  activeTab: 0,
};

const advanceSearchSlice = createSlice({
  name: 'advanceSearch',
  initialState,
  reducers: {
    triggerAdvanceSearch(state, action: PayloadAction<AdvanceSearchRequest>) {
      const previousSearchType = state.searchType;
      const isFirstPage = !action.payload.page || action.payload.page <= 1;
      const searchTypeChanged = action.payload.searchType !== previousSearchType;
      state.searchType = action.payload.searchType;
      state.searchParams = action.payload;
      state.page = action.payload.page ?? 1;
      state.isLoading = true;
      state.isPagination = !isFirstPage;
      state.activeTab = searchTypeToTabIndex(action.payload.searchType);
      if (isFirstPage || searchTypeChanged) {
        state.isLoaded = false;
        state.errorText = '';
        state.isPersonExtendedLoading = action.payload.searchType === 'person';
        state.isPersonExtendedLoaded = action.payload.searchType !== 'person';
        state.peopleSection = { ...initialState.peopleSection };
        state.companiesSection = { ...initialState.companiesSection };
        state.buyerGroupsSection = { ...initialState.buyerGroupsSection };
      }
    },
    advanceSearchLoaded(state, action: PayloadAction<SearchResultSection[]>) {
      state.isLoading = false;
      state.isLoaded = true;
      state.isPagination = false;

      action.payload
        ?.filter((s) => s.type === SectionTypes.Executives)
        .forEach((section) => {
          state.peopleSection.title = section.title;
          state.peopleSection.persons = [
            ...(state.peopleSection.persons ?? []),
            ...(section.persons ?? []),
          ];
          state.peopleSection.count = section.totalCount ?? section.count;
        });

      action.payload
        ?.filter((s) => s.type === SectionTypes.Companies)
        .forEach((section) => {
          state.companiesSection.title = section.title;
          state.companiesSection.companies = [
            ...(state.companiesSection.companies ?? []),
            ...(section.companies ?? []),
          ];
          state.companiesSection.count = section.totalCount ?? section.count;
        });

      action.payload
        ?.filter((s) => s.type === SectionTypes.BuyerGroups)
        .forEach((section) => {
          state.buyerGroupsSection.title = section.title;
          state.buyerGroupsSection.groups = [
            ...(state.buyerGroupsSection.groups ?? []),
            ...(section.groups ?? []),
          ];
          state.buyerGroupsSection.count = section.totalCount ?? section.count;
        });
    },
    advanceSearchPersonExtendedLoaded(state, action: PayloadAction<PersonLinkedinSearch>) {
      state.isPersonExtendedLoading = false;
      state.isPersonExtendedLoaded = true;
      state.peopleSection.persons = [
        ...(state.peopleSection.persons ?? []),
        ...action.payload.results,
      ];
      if (action.payload.count > 0) {
        state.peopleSection.count += action.payload.count;
      }
    },
    advanceSearchPersonExtendedDone(state) {
      state.isPersonExtendedLoading = false;
      state.isPersonExtendedLoaded = true;
    },
    advanceSearchError(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.isLoaded = true;
      state.isPagination = false;
      state.errorText = action.payload;
      state.isPersonExtendedLoading = false;
      state.isPersonExtendedLoaded = true;
    },
    advanceSearchReset() {
      return { ...initialState };
    },
    getAdvanceSearchMembers(state, action: PayloadAction<{ offset: number; searchTerm?: string }>) {
      state.memberLoading = true;
      if (action.payload.offset === 0) {
        state.memberOptions = [];
        state.memberTotal = 0;
        state.memberHasMore = false;
        state.memberOffset = 0;
      }
    },
    setAdvanceSearchMembers(
      state,
      action: PayloadAction<{ options: AdvanceSearchDropdownOption[]; total: number; offset: number }>,
    ) {
      const { options, total, offset } = action.payload;
      state.memberOptions = offset === 0
        ? options
        : state.memberOptions.concat(options);
      state.memberTotal = total;
      state.memberOffset = offset + options.length;
      state.memberHasMore = offset + options.length < total;
      state.memberLoading = false;
      state.memberOptionsLoaded = true;
    },
    advanceSearchDropdownError(state) {
      state.memberLoading = false;
    },
    setAdvanceSearchPeopleForm(state, action: PayloadAction<AdvanceSearchPeopleForm>) {
      state.peopleForm = action.payload;
    },
    setAdvanceSearchCompanyForm(state, action: PayloadAction<AdvanceSearchCompanyForm>) {
      state.companyForm = action.payload;
    },
    setAdvanceSearchBrmForm(state, action: PayloadAction<AdvanceSearchBrmForm>) {
      state.brmForm = action.payload;
    },
    setMemberSearchTerm(state, action: PayloadAction<string>) {
      state.memberSearchTerm = action.payload;
    },
    clearAllForms(state) {
      state.peopleForm = { ...initialState.peopleForm };
      state.companyForm = { ...initialState.companyForm };
      state.brmForm = { ...initialState.brmForm };
      state.memberSearchTerm = '';
    },
    setAdvanceSearchActiveTab(state, action: PayloadAction<number>) {
      state.activeTab = action.payload;
    },
    getAdvanceSearchMetadata(state) {
      state.advanceSearchMetadataLoading = true;
    },
    advanceSearchMetadataLoaded(state, action: PayloadAction<AdvancedSearchMetadataRecord>) {
      state.advanceSearchMetadataLoading = false;
      state.advanceSearchMetadata = action.payload;
    },
    searchFollowHandling(state, action: PayloadAction<number>) {
      const { payload } = action;
      state.peopleSection?.persons?.forEach((person: Executive) => {
        if (person.personId === payload) {
          person.personAdded = true;
          state.peopleSection.followedPersons?.push(person);
        }
      });
    },
    searchUnfollowHandling(state, action: PayloadAction<number[]>) {
      const { payload } = action;
      state.peopleSection?.persons?.forEach((person: Executive) => {
        if (payload.includes(person.personId)) {
          person.personAdded = false;
        }
      });
      payload.forEach((personId) => {
        const index = state.peopleSection.followedPersons?.findIndex(
          (followedPerson: Executive) => followedPerson.personId === personId,
        );
        if (index !== undefined && index !== -1) {
          state.peopleSection.followedPersons?.splice(index, 1);
        }
      });
    },
    searchCompanyFollowLoading(
      state,
      action: PayloadAction<FollowCompanyRequest>,
    ) {
      const { companyId } = action.payload;
      const index = state?.companiesSection?.companies?.findIndex(
        (company: Company) => company.companyId === parseInt(companyId.trim(), 10),
      );
      if (index !== -1 && index !== undefined && state.companiesSection?.companies) {
        state.companiesSection.companies[index].loading = true;
      }
      const indexOfFollowed = state?.companiesSection?.followedCompanies?.findIndex(
        (company: Company) => company.companyId === parseInt(companyId.trim(), 10),
      );
      if (
        indexOfFollowed !== -1
        && indexOfFollowed !== undefined
        && state?.companiesSection?.followedCompanies?.length
      ) {
        state.companiesSection.followedCompanies[indexOfFollowed].loading = true;
      }
    },
    searchCompaniesFollowHandling(
      state,
      action: PayloadAction<FollowCompanyResponse>,
    ) {
      const { payload } = action;
      const { companyIds, popUpResponse } = payload.response;
      const { companies } = state.companiesSection;
      const { followedCompanies } = state.companiesSection;

      companyIds.forEach((companyId) => {
        const index = companies?.findIndex(
          (company: Company) => String(company?.companyId) === companyId,
        );
        if (index !== -1 && index !== undefined && companies) {
          if (popUpResponse.showPlanPopup !== 8) {
            followedCompanies?.push(companies[index]);
            companies[index].loading = false;
            companies[index].companySubscribed = true;
            companies[index].companySubscribers += 1;
          } else {
            companies[index].loading = false;
          }
        }
      });
      state.companiesSection.companies = companies;
      state.companiesSection.followedCompanies = followedCompanies;
    },
    searchCompaniesUnfollowHandling(state, action: PayloadAction<string[]>) {
      const { payload } = action;
      const { companies } = state.companiesSection;
      const { followedCompanies } = state.companiesSection;
      companies?.forEach((company: Company) => {
        if (payload.includes(String(company.companyId))) {
          company.companySubscribed = false;
          company.loading = false;
        }
      });
      payload.forEach((companyId) => {
        const index = followedCompanies?.findIndex(
          (followedCompany: Company) => followedCompany.companyId === parseInt(companyId, 10),
        );
        if (index !== undefined && index !== -1) {
          followedCompanies?.splice(index, 1);
        }
        const indexOfCompaniesSection = companies?.findIndex(
          (company: Company) => String(company?.companyId) === companyId,
        ) || 0;
        if (companies && indexOfCompaniesSection >= 0) {
          companies[indexOfCompaniesSection].companySubscribers -= 1;
        }
      });
      state.companiesSection.companies = companies;
      state.companiesSection.followedCompanies = followedCompanies;
    },
  },
});

export const { actions, reducer, name: sliceKey } = advanceSearchSlice;
